'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';

interface ActionButtonsProps {
  product: {
    id: string;
    brand: string;
    model: string;
    price: number;
    originalPrice: number;
    condition: string;
    storage: string;
    color: string;
    carrier: string;
  };
  images: Array<{ url: string; alt: string }>;
  seller: {
    name: string;
    verified: boolean;
    rating: number;
  };
}

const ActionButtons = ({ product, images, seller }: ActionButtonsProps) => {
  const router = useRouter();
  const { addToCart, cartItems } = useCart();
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const isInCart = cartItems.some((item) => item.id === product.id);

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      brand: product.brand,
      model: product.model,
      image: images[0]?.url || '',
      alt: images[0]?.alt || `${product.brand} ${product.model}`,
      price: product.price,
      originalPrice: product.originalPrice,
      condition: product.condition,
      storage: product.storage,
      color: product.color,
      carrier: product.carrier,
      seller: {
        name: seller.name,
        verified: seller.verified,
        rating: seller.rating,
      },
    });

    setShowAddedMessage(true);
    setTimeout(() => setShowAddedMessage(false), 3000);
  };

  const handleBuyNow = () => {
    if (!isInCart) {
      handleAddToCart();
    }
    router.push('/shopping-cart');
  };

  return (
    <div data-component-id="src/app/product-details/components/ActionButtons.tsx:68:4" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="68" data-component-end-line="112" data-component-file="ActionButtons.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
      {/* Add to Cart Success Message */}
      {showAddedMessage && (
        <div data-component-id="src/app/product-details/components/ActionButtons.tsx:71:8" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="71" data-component-end-line="74" data-component-file="ActionButtons.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-fade-in%20rounded-lg%20bg-success%2F10%20border%20border-success%20p-3%20flex%20items-center%20gap-2%22%7D" className="animate-fade-in rounded-lg bg-success/10 border border-success p-3 flex items-center gap-2">
          <Icon data-component-id="src/app/product-details/components/ActionButtons.tsx:72:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="72" data-component-end-line="72" data-component-file="ActionButtons.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%22%7D" name="CheckCircleIcon" size={20} className="text-success" />
          <span data-component-id="src/app/product-details/components/ActionButtons.tsx:73:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="73" data-component-end-line="73" data-component-file="ActionButtons.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-success%22%2C%22textContent%22%3A%22Added%20to%20cart%20successfully!%22%7D" className="text-sm font-medium text-success">Added to cart successfully!</span>
        </div>
      )}

      {/* Action Buttons */}
      <div data-component-id="src/app/product-details/components/ActionButtons.tsx:78:6" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="78" data-component-end-line="111" data-component-file="ActionButtons.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%22%7D" className="flex gap-3">
        <button data-component-id="src/app/product-details/components/ActionButtons.tsx:79:8" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="79" data-component-end-line="90" data-component-file="ActionButtons.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          onClick={handleAddToCart}
          disabled={isInCart}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold transition-smooth focus-ring ${
            isInCart
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground'
          }`}
        >
          <Icon data-component-id="src/app/product-details/components/ActionButtons.tsx:88:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="88" data-component-end-line="88" data-component-file="ActionButtons.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShoppingCartIcon%22%7D" name="ShoppingCartIcon" size={20} />
          <span data-component-id="src/app/product-details/components/ActionButtons.tsx:89:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="89" data-component-end-line="89" data-component-file="ActionButtons.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{isInCart ? 'In Cart' : 'Add to Cart'}</span>
        </button>

        <button data-component-id="src/app/product-details/components/ActionButtons.tsx:92:8" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="92" data-component-end-line="98" data-component-file="ActionButtons.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20flex-1%20items-center%20justify-center%20gap-2%20rounded-lg%20bg-primary%20px-6%20py-3%20font-semibold%20text-primary-foreground%20shadow-elevation-1%20transition-smooth%20hover%3Abg-primary%2F90%20focus-ring%22%7D"
          onClick={handleBuyNow}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-elevation-1 transition-smooth hover:bg-primary/90 focus-ring"
        >
          <Icon data-component-id="src/app/product-details/components/ActionButtons.tsx:96:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="96" data-component-end-line="96" data-component-file="ActionButtons.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22BoltIcon%22%7D" name="BoltIcon" size={20} />
          <span data-component-id="src/app/product-details/components/ActionButtons.tsx:97:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="97" data-component-end-line="97" data-component-file="ActionButtons.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Buy%20Now%22%7D">Buy Now</span>
        </button>

        <button data-component-id="src/app/product-details/components/ActionButtons.tsx:100:8" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="100" data-component-end-line="110" data-component-file="ActionButtons.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20rounded-lg%20border%20border-border%20bg-card%20px-4%20py-3%20transition-smooth%20hover%3Aborder-primary%20hover%3Abg-primary%2F10%20focus-ring%22%7D"
          onClick={() => setIsFavorite(!isFavorite)}
          className="flex items-center justify-center rounded-lg border border-border bg-card px-4 py-3 transition-smooth hover:border-primary hover:bg-primary/10 focus-ring"
        >
          <Icon data-component-id="src/app/product-details/components/ActionButtons.tsx:104:10" data-component-path="src/app/product-details/components/ActionButtons.tsx" data-component-line="104" data-component-end-line="109" data-component-file="ActionButtons.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22HeartIcon%22%7D"
            name="HeartIcon"
            size={24}
            variant={isFavorite ? 'solid' : 'outline'}
            className={isFavorite ? 'text-error' : 'text-foreground'}
          />
        </button>
      </div>
    </div>
  );
};

export default ActionButtons;